import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { emptyArray } from 'ui-components/default-props/empty';

import { isPremiumPlan, isStandardPlan, isNewLineupPlan } from '../../enums/billing-plan-ids';
import Global from '../../store/global-selectors';
import { selectReferenceDataShipmentTopupTiers } from '../../store/reference-data/reference-data.selectors';
import { useShipmentUsageSummary } from './use-shipment-usage-summary';

import type { LimitTier, ManageSubscriptionPlan } from '@shipstation/client-common/api/manage-subscription.api';
import type { ManageSubscriptionDrawerProps } from '../../contexts/modal-context/global-modal-context';
import { useEnablePurchaseAdditionalShipments } from '../../feature-flags/hooks/use-enable-purchase-additional-shipments';

const MIN_TOPUP_ELIGIBLE_SHIPMENT_LIMIT = 500;

export const useCanPurchaseShipmentVolume = () => {
  const flagValue = useEnablePurchaseAdditionalShipments();
  const billingPlanId = useSelector(Global.selectSellerSubscriptionPlanInfo)?.billingPlanId;
  const topupTiers = useSelector(selectReferenceDataShipmentTopupTiers);

  const isValidPlan = isStandardPlan(billingPlanId) || isPremiumPlan(billingPlanId);
  const hasTiersAvailable = topupTiers.length > 0;
  const canPurchaseVolume = flagValue && isValidPlan && hasTiersAvailable;

  const value = useMemo(
    () => ({
      isValidPlan,
      hasTiersAvailable,
      canPurchaseVolume,
    }),
    [canPurchaseVolume, isValidPlan, hasTiersAvailable],
  );

  return value;
};

export const useShipmentVolumeUpgrade = () => {
  const plan = useSelector(Global.selectSellerPlanInfo)?.plan;
  const allPlans: ManageSubscriptionPlan[] = useSelector(Global.selectSellerSubscriptionData)?.allPlans ?? emptyArray;
  const baseShipmentCount = useSelector(Global.selectSellerShipmentUsage)?.baseShipmentCount;
  const { isValidPlan } = useCanPurchaseShipmentVolume();

  const planShipmentLimit = plan?.shipmentLimit?.limit ?? baseShipmentCount ?? 0;
  const planTiers: LimitTier[] = plan?.shipmentLimit?.tiers ?? emptyArray;
  const maxPlanShipmentLimit = planTiers.length > 0 ? Math.max(...planTiers.map((tier) => tier.endingQuantity)) : planShipmentLimit;

  const nextPlanTier = useMemo(() => {
    const currentPricePerMonth = plan?.pricePerMonth?.value ?? 0;
    return allPlans
      .filter((candidate) => candidate.isOnAnnualBilling === (plan?.isOnAnnualBilling ?? false))
      .filter((candidate) => isStandardPlan(candidate.billingPlanId) || isPremiumPlan(candidate.billingPlanId))
      .filter((candidate) => candidate.pricePerMonth.value > currentPricePerMonth)
      .sort((a, b) => a.pricePerMonth.value - b.pricePerMonth.value)
      .at(0);
  }, [allPlans, plan]);

  const nextShipmentVolumeTier = useMemo(
    () =>
      planTiers
        .map((tier) => tier.endingQuantity)
        .filter((endingQuantity) => endingQuantity > planShipmentLimit)
        .sort((a, b) => a - b)
        .at(0) ?? MIN_TOPUP_ELIGIBLE_SHIPMENT_LIMIT,
    [planTiers, planShipmentLimit],
  );

  const upgradeDrawerProps: ManageSubscriptionDrawerProps = useMemo(
    () =>
      isValidPlan
        ? { selectedPlanId: plan?.billingPlanId, desiredShipmentLimit: nextShipmentVolumeTier }
        : { selectedPlanId: nextPlanTier?.billingPlanId },
    [isValidPlan, nextPlanTier, nextShipmentVolumeTier, plan?.billingPlanId],
  );

  return useMemo(() => ({ maxPlanShipmentLimit, upgradeDrawerProps }), [maxPlanShipmentLimit, upgradeDrawerProps]);
};

export const useShouldShowAdditionalShipmentVolume = (): boolean => {
  const flagValue = useEnablePurchaseAdditionalShipments();
  const billingPlanId = useSelector(Global.selectSellerSubscriptionPlanInfo)?.billingPlanId;
  const { isUnlimitedShipments } = useShipmentUsageSummary();

  return flagValue && !isUnlimitedShipments && isNewLineupPlan(billingPlanId);
};
